import deepEqual from 'fast-deep-equal';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { ORDER_TABLE_HEADERS } from 'utils/constants/Order';
import { SHIPMENT_TABLE_HEADERS } from 'utils/constants/Shipment';
import { FilterContext, ListContext } from 'utils/context';
import { TFilterValue } from 'utils/context/FilterContext/types';
import { useSavedOrders, useSavedShipments } from 'utils/hooks';
import { ISavedViewsHook } from 'utils/hooks/useSavedViews/useSavedViews';

import { TfindView, TView, TViewContext, TViewType } from './types';

const defaultSelected = (name: TFilterValue): TView => {
  const isOrder = name === 'all-orders';
  const listView = isOrder ? 'all-orders' : 'all-shipments';
  const viewType = isOrder ? 'order' : 'shipment';
  return {
    selectedView: {
      id: listView,
      name: '',
      filterCriteria: {
        listView,
        viewType,
      },
      displayOptions: { activeColumns: [] },
    },
    viewType,
    cache: {
      listView,
      viewType,
    },
  };
};

const defaultSavedView: ISavedViewsHook = {
  data: { views: [] },
  isError: false,
  isLoading: false,
  createView: () => new Promise(() => ({})),
  deleteView: () => new Promise(() => ({})),
  sortViews: () => new Promise(() => ({})),
  updateView: () => new Promise(() => ({})),
};

const ViewContext = createContext<TViewContext>({
  canUpdate: false,
  getViewRequest: () => ({}),
  revertView: () => false,
  savedOrders: defaultSavedView,
  savedShipments: defaultSavedView,
  selectedView: undefined,
  setWillSearch: () => null,
  viewType: 'shipment' as TViewType,
});

interface IViewContextProvider {
  children: React.ReactNode;
}

function ViewContextProvider({ children }: IViewContextProvider) {
  const {
    filters,
    hasFilters,
    getOrdersRequest,
    getShipmentsRequest,
    paramsFromOrder,
    paramsFromShipment,
    setFilters,
  } = useContext(FilterContext);

  const { doSearchOrders, doSearchShipments, doCountOrders, doCountShipments } =
    useContext(ListContext);

  const savedShipments = useSavedShipments();
  const savedOrders = useSavedOrders();
  const isLoading = savedShipments.isLoading || savedOrders.isLoading;

  const [view, setView] = useState(defaultSelected(filters.listView));
  const [didMount, setDidMount] = useState(false);
  const [willSearch, setWillSearch] = useState(false);

  const { selectedView, viewType } = view;

  const findViews = useCallback(() => {
    const findView: TfindView = (views, viewType, getParams) => {
      const listView = filters.listView;
      const isOrder = viewType === 'order';
      const found = views.find(({ id }) => id === listView);

      if (found) {
        const current = found.displayOptions;
        const activeColumns = current?.activeColumns || [];
        const ids = activeColumns.map(({ id }) => id);
        const headers = isOrder ? ORDER_TABLE_HEADERS : SHIPMENT_TABLE_HEADERS;

        const columns = headers.map(({ id, label }) => ({
          id,
          label,
          enabled: ![
            'current-stop',
            'connection-status',
            'shared-with',
            'visibility-provided',
            'est-act-departure',
            'planned-departure',
            'poo-port-name',
            'poo-departure-time',
            'poo-planned-departure',
            'pod-port-name',
            'pod-est-act-arrival',
            'pod-planned-arrival',
          ].includes(id),
        }));

        columns.forEach((column) => {
          if (!ids.includes(column.id)) {
            activeColumns.push(column);
          }
        });

        const sortActiveColumns = (current: number, target: number) => {
          activeColumns.splice(current, 0, activeColumns.splice(target, 1)[0]);
        };

        const sortConfig: { [key: string]: string[] } = {
          origin: ['est-act-departure', 'planned-departure'],
        };

        if (!isOrder) {
          for (const [key, value] of Object.entries(sortConfig)) {
            for (let index = 0; index < value.length; index++) {
              const ids = activeColumns.map(({ id }) => id);
              const currentIndex = ids.indexOf(value[index]);
              const targetIndex = ids.indexOf(key);
              if (currentIndex > targetIndex) {
                sortActiveColumns(targetIndex + index + 1, currentIndex);
              } else {
                sortActiveColumns(targetIndex, currentIndex);
              }
            }
          }
        }

        return {
          cache: {
            listView,
            viewType: isOrder ? 'order' : 'shipment',
            ...getParams(found.filterCriteria),
          },
          selectedView: { ...found, displayOptions: { activeColumns } },
          viewType,
        };
      }
    };

    const orders = savedOrders.data.views;
    const shipments = savedShipments.data.views;
    const order = findView(orders, 'order', paramsFromOrder);
    const shipment = findView(shipments, 'shipment', paramsFromShipment);
    return order || shipment;
  }, [
    filters.listView,
    paramsFromOrder,
    paramsFromShipment,
    savedOrders.data.views,
    savedShipments.data.views,
  ]);

  useEffect(() => {
    if (!isLoading) {
      if (!didMount) {
        const found = findViews();

        const fallback =
          filters.viewType === 'order' || filters.listView === 'all-orders'
            ? 'all-orders'
            : 'all-shipments';
        const nextListView = found?.selectedView?.id || fallback;
        const nextView = found || defaultSelected(nextListView);

        if (hasFilters(ALL_FILTERS)) {
          setFilters((prev) => ({
            ...prev,
            listView: nextListView,
            viewType: nextView.viewType,
          }));
        } else {
          setFilters({
            ...nextView.cache,
            viewType: nextView.viewType,
          });
        }
        setDidMount(true);
        setView(nextView);
        setWillSearch(true);
      } else if (filters.listView !== selectedView.id) {
        const found = findViews();
        const nextListView = filters.listView || 'all-shipments';
        const nextView = found || defaultSelected(nextListView);
        setFilters({
          ...nextView.cache,
          viewType: nextView.viewType,
        });
        setView(nextView);
        setWillSearch(true);
      }
    }
  }, [
    didMount,
    filters.listView,
    filters.viewType,
    findViews,
    hasFilters,
    isLoading,
    selectedView.id,
    setFilters,
  ]);

  useEffect(() => {
    if (willSearch) {
      setWillSearch(false);
      doCountOrders();
      doCountShipments();

      if (viewType === 'order') {
        doSearchOrders();
      } else {
        const active = selectedView?.displayOptions?.activeColumns || [];
        const added = active.filter(({ id }) => /ref-/i.test(id));
        const addedLabels = added.map(({ label }) => label);
        doSearchShipments(undefined, addedLabels);
      }
    }
  }, [
    doCountOrders,
    doCountShipments,
    doSearchOrders,
    doSearchShipments,
    selectedView.displayOptions,
    viewType,
    willSearch,
  ]);

  const values = useMemo(() => {
    const isOrder = viewType === 'order';
    return {
      canUpdate: !deepEqual(view.cache, filters),
      getViewRequest: isOrder ? getOrdersRequest : getShipmentsRequest,
      revertView: () => setFilters(view.cache),
      savedOrders,
      savedShipments,
      selectedView,
      setWillSearch: () => setWillSearch(true),
      viewType,
    };
  }, [
    filters,
    getOrdersRequest,
    getShipmentsRequest,
    savedOrders,
    savedShipments,
    selectedView,
    setFilters,
    view.cache,
    viewType,
  ]);

  return <ViewContext.Provider value={values}>{children}</ViewContext.Provider>;
}

export { ViewContext, ViewContextProvider };
